
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Play, Pause, RotateCcw, ChevronRight } from 'lucide-react';
import { SimulationStep } from './types';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface SimulationControlsProps {
  isRunning: boolean;
  onStart: () => void;
  onPause: () => void;
  onReset: () => void;
  progress: number;
  currentStep: number;
  simulationSteps: SimulationStep[];
  simulationCompleted: boolean;
}

export const SimulationControls = ({
  isRunning,
  onStart,
  onPause,
  onReset,
  progress,
  currentStep,
  simulationSteps,
  simulationCompleted
}: SimulationControlsProps) => {
  const isMobile = useIsMobile();
  
  return (
    <Card>
      <CardHeader className="py-3 md:py-4">
        <CardTitle className="text-base">Simulation Controls</CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 md:px-4 md:pb-4 pt-0 space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        
        <div className={cn(
          isMobile ? "grid grid-cols-3 gap-2" : "flex space-x-2"
        )}>
          {!isRunning ? (
            <Button 
              onClick={onStart} 
              className={cn(
                "flex-1",
                isMobile && "col-span-2"
              )}
              disabled={simulationCompleted}
            >
              <Play className="h-4 w-4 mr-1" />
              {simulationCompleted ? "Completed" : "Start"}
            </Button>
          ) : (
            <Button 
              onClick={onPause} 
              variant="secondary" 
              className={cn(
                "flex-1",
                isMobile && "col-span-2"
              )}
            >
              <Pause className="h-4 w-4 mr-1" />
              Pause
            </Button>
          )}
          <Button 
            onClick={onReset} 
            variant="outline" 
            className="flex-1"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        </div>
        
        <div className="space-y-2 pt-2">
          <h4 className="text-sm font-medium">Current Step:</h4>
          <div className={cn(
            "rounded-md border p-3 bg-muted/30 dark:bg-slate-800/30",
            currentStep > 0 && "border-primary"
          )}>
            {currentStep < simulationSteps.length ? (
              <div className="flex items-start">
                <div className="mr-2 flex-shrink-0 p-1 bg-primary rounded-full">
                  <ChevronRight className="h-3 w-3 text-primary-foreground" />
                </div>
                <div>
                  <p className="font-medium text-sm">
                    {simulationSteps[currentStep]?.name || "Not started"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {simulationSteps[currentStep]?.description || "Select entities and press Start to begin the simulation"}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center text-sm text-muted-foreground">
                Simulation complete
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
